import type { Locale } from "./Locale";
import type { GettextInterface } from "@indeed/indeed-gettext";

/**
 * Wraps a gettext instance so it never leaks untranslated English to non-English
 * users: new copy reaches production before its translations, and a plain
 * gettext call returns the English msgid until then. Used by `useSafeI18n`.
 *
 * - **English** (source locale, incl. `en_GB`/`en_CA`/bare-`en`): returned
 *   unchanged — gettext is already correct for every msgid.
 * - **Any other locale**: a msgid renders only when the catalog marks it
 *   translated; otherwise an empty string (`split.*` → empty array), which keeps
 *   the `string` return type so it stays a drop-in.
 *
 * Permanent — as translations arrive, each locale upgrades with no code change.
 */
export function makeSafeI18n(i18n: GettextInterface, locale: Locale): GettextInterface {
  // English is the source locale; gettext is already correct for every msgid.
  if (locale.language === "en") {
    return i18n;
  }

  const isTranslated = (msgCtxt: string | undefined, msgId: string): boolean =>
    i18n.findTranslation(msgCtxt, msgId).isTranslated;

  return {
    ...i18n,
    gettext(msgId, args) {
      return isTranslated(undefined, msgId) ? i18n.gettext(msgId, args) : "";
    },
    pgettext(msgCtxt, msgId, args) {
      return isTranslated(msgCtxt, msgId) ? i18n.pgettext(msgCtxt, msgId, args) : "";
    },
    ngettext(msgId, msgIdPlural, n, args) {
      return isTranslated(undefined, msgId) ? i18n.ngettext(msgId, msgIdPlural, n, args) : "";
    },
    npgettext(msgCtxt, msgId, msgIdPlural, n, args) {
      return isTranslated(msgCtxt, msgId)
        ? i18n.npgettext(msgCtxt, msgId, msgIdPlural, n, args)
        : "";
    },
    split: {
      ...i18n.split,
      gettext(msgId, args) {
        return isTranslated(undefined, msgId) ? (i18n.split.gettext(msgId, args) as unknown[]) : [];
      },
      pgettext(msgCtxt, msgId, args) {
        return isTranslated(msgCtxt, msgId)
          ? (i18n.split.pgettext(msgCtxt, msgId, args) as unknown[])
          : [];
      },
      ngettext(msgId, msgIdPlural, n, args) {
        return isTranslated(undefined, msgId)
          ? (i18n.split.ngettext(msgId, msgIdPlural, n, args) as unknown[])
          : [];
      },
      npgettext(msgCtxt, msgId, msgIdPlural, n, args) {
        return isTranslated(msgCtxt, msgId)
          ? (i18n.split.npgettext(msgCtxt, msgId, msgIdPlural, n, args) as unknown[])
          : [];
      },
    },
  };
}
